// Receipt printer — tries silent WebUSB ESC/POS first, falls back to system HTML print.
import { fmtSDG } from "@/lib/api";
import { autoReconnect, isPrinterConnected, printDOMElement } from "@/lib/escpos";

function buildReceiptHTML({ order, settings, lang }) {
    const isAr = lang === "ar";
    const name = (isAr ? settings.cafe_name_ar : settings.cafe_name_en) || "Café 24";
    const footer = (isAr ? settings.receipt_footer_ar : settings.receipt_footer_en) || "";
    const itemsHTML = order.items.map((it) => `
      <div class="row">
        <span>${it.qty}× ${isAr ? it.name_ar : it.name_en}</span>
        <span>${fmtSDG(it.price * it.qty, lang)}</span>
      </div>`).join("");
    return `
      <div class="receipt">
        <div class="center">
          <h1>${name}</h1>
          ${settings.address ? `<div class="small">${settings.address}</div>` : ""}
          ${settings.phone ? `<div class="small">${settings.phone}</div>` : ""}
        </div>
        <div class="sep"></div>
        <div class="row small"><span>${isAr ? "طلب" : "Order"} #${order.order_no}</span><span>${isAr ? "طاولة" : "Table"} ${order.table_number}</span></div>
        <div class="row small"><span>${new Date(order.created_at).toLocaleString(isAr ? "ar-SD" : "en-US")}</span></div>
        <div class="row small"><span>${isAr ? "الويتر" : "Waiter"}: ${order.waiter_name || "—"}</span><span>${isAr ? "كاشير" : "Cashier"}: ${order.cashier_name || "—"}</span></div>
        <div class="sep"></div>
        ${itemsHTML}
        <div class="sep"></div>
        <div class="row"><span>${isAr ? "الفرعي" : "Subtotal"}</span><span>${fmtSDG(order.subtotal, lang)}</span></div>
        ${order.discount_amount > 0 ? `<div class="row"><span>${isAr ? "خصم" : "Discount"}${order.discount_percent ? ` (${order.discount_percent}%)` : ""}${order.discount_reason ? ` · ${order.discount_reason}` : ""}</span><span>− ${fmtSDG(order.discount_amount, lang)}</span></div>` : ""}
        ${order.delivery_fee > 0 ? `<div class="row"><span>${isAr ? "رسوم التوصيل" : "Delivery"}</span><span>${fmtSDG(order.delivery_fee, lang)}</span></div>` : ""}
        ${order.tax > 0 ? `<div class="row"><span>${isAr ? "الضريبة" : "Tax"}</span><span>${fmtSDG(order.tax, lang)}</span></div>` : ""}
        <div class="row total"><span>${isAr ? "الإجمالي" : "TOTAL"}</span><span>${fmtSDG(order.total, lang)}</span></div>
        <div class="sep"></div>
        ${footer ? `<div class="center small">${footer}</div>` : ""}
        <div class="center small" style="margin-top:6px;">${isAr ? "شكراً لاختياركم كافيه ٢٤" : "Thank you!"}</div>
      </div>`;
}

function receiptStyles({ forPrintWindow, isAr }) {
    const fontImport = forPrintWindow ? `@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&family=Work+Sans:wght@400;600&display=swap');` : "";
    return `
      ${fontImport}
      .receipt { width: ${forPrintWindow ? "80mm" : "576px"}; padding: ${forPrintWindow ? "8px" : "20px 24px"}; box-sizing: border-box; background: #fff; color: #000; font-family: ${isAr ? "'Cairo'" : "'Work Sans'"}, monospace; letter-spacing: 0 !important; }
      .receipt h1 { text-align: center; font-size: ${forPrintWindow ? "18px" : "44px"}; margin: 4px 0; font-weight: 700; letter-spacing: 0; }
      .receipt .small { font-size: ${forPrintWindow ? "11px" : "22px"}; }
      .receipt .row { display: flex; justify-content: space-between; font-size: ${forPrintWindow ? "12px" : "24px"}; padding: ${forPrintWindow ? "2px 0" : "4px 0"}; }
      .receipt .row span { display: inline-block; max-width: 70%; }
      .receipt .sep { border-top: ${forPrintWindow ? "1px" : "3px"} dashed #000; margin: ${forPrintWindow ? "6px 0" : "12px 0"}; }
      .receipt .total { font-size: ${forPrintWindow ? "16px" : "32px"}; font-weight: 700; padding-top: ${forPrintWindow ? "4px" : "8px"}; }
      .receipt .center { text-align: center; }
    `;
}

async function tryUSBPrint({ order, settings, lang }) {
    if (!isPrinterConnected()) await autoReconnect();
    if (!isPrinterConnected()) return false;
    const isAr = lang === "ar";
    const container = document.createElement("div");
    container.style.cssText = "position:fixed;left:-99999px;top:0;width:576px;background:#fff;";
    container.setAttribute("dir", isAr ? "rtl" : "ltr");
    container.innerHTML = `<style>${receiptStyles({ forPrintWindow: false, isAr })}</style>${buildReceiptHTML({ order, settings, lang })}`;
    document.body.appendChild(container);
    try {
        // allow Cairo font to apply
        await new Promise((r) => setTimeout(r, 80));
        await printDOMElement(container.querySelector(".receipt") || container);
        return true;
    } finally {
        document.body.removeChild(container);
    }
}

async function printViaWindow({ order, settings, lang }) {
    const isAr = lang === "ar";
    // Render the receipt to a canvas IMAGE first so Arabic prints as pure pixels
    // (avoids missing fonts on Xprinter / generic thermal printer drivers).
    const html2canvas = (await import("html2canvas")).default;
    const offscreen = document.createElement("div");
    offscreen.style.cssText = "position:fixed;left:-99999px;top:0;width:576px;background:#fff;";
    offscreen.setAttribute("dir", isAr ? "rtl" : "ltr");
    offscreen.innerHTML = `<style>${receiptStyles({ forPrintWindow: false, isAr })}</style>${buildReceiptHTML({ order, settings, lang })}`;
    document.body.appendChild(offscreen);
    let dataUrl;
    try {
        // wait for Cairo font to be fully ready so Arabic shapes connect properly
        if (document.fonts && document.fonts.ready) {
            try { await document.fonts.ready; } catch (e) { /* ignore */ }
        }
        await new Promise((r) => setTimeout(r, 250));
        const canvas = await html2canvas(offscreen.querySelector(".receipt") || offscreen, {
            backgroundColor: "#ffffff",
            width: 576,
            scale: 1,
            useCORS: true,
            logging: false,
        });
        dataUrl = canvas.toDataURL("image/png");
    } finally {
        document.body.removeChild(offscreen);
    }

    const html = `
<!doctype html><html lang="${lang}"><head>
<meta charset="utf-8"/><title>Receipt #${order.order_no}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { background: #fff; }
  img.receipt-img { display: block; width: 80mm; height: auto; }
  @media print {
    @page { size: 80mm auto; margin: 0; }
    body { width: 80mm; }
  }
</style></head><body>
<img class="receipt-img" src="${dataUrl}" alt="Receipt" />
<script>
  const img = document.querySelector("img");
  if (img.complete) { window.print(); setTimeout(()=>window.close(),600); }
  else img.onload = () => { window.print(); setTimeout(()=>window.close(),600); };
</script>
</body></html>`;
    const w = window.open("", "_blank", "width=380,height=720");
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
}

export async function printReceipt({ order, settings, lang = "en" }) {
    // 1) silent USB print first
    try {
        const ok = await tryUSBPrint({ order, settings, lang });
        if (ok) return { method: "usb" };
    } catch (e) {
        console.warn("USB print failed, falling back:", e);
    }
    // 2) fallback: image-based print via system dialog (Arabic-safe)
    await printViaWindow({ order, settings, lang });
    return { method: "html" };
}
